import { flow } from "mobx";
import { observer } from "mobx-react";
import React, { useContext } from "react";
import { useQueryClient } from "react-query";
import { useLocStore } from "../../../../wsLocalization";
import { LocContext } from "../../../../wsLocalization/LocContext";
import { invalidateQueries } from "../../../../WSUtils/useLatestQuery";
import { UserInfoContext } from "../../../../WSUtils/UserInfoContext";
import { wsConfirmDialog } from "../../../../WSUtils/wsConfirmDialog";
import { CVEditContext } from "../../CVEditContext";
import { CVEditDeleteDocumentButton } from "../Components/CVEditDeleteDocumentButton";
import { CVEditDocument } from "../Components/CVEditDocument";
import { CVEditDocumentBaseList } from "../Components/CVEditDocumentsBaseList";
import { CVEditDownloadDocumentButton } from "../Components/CVEditDownloadDocumentButton";
import { CVEditDocumentsSegmentStore } from "./CVEditDocumentSegmentStore";

export interface CVEditDocumentListProps {
  documentsSegmentStore: CVEditDocumentsSegmentStore;
}
export const CVEditDocumentList = observer(
  ({ documentsSegmentStore }: CVEditDocumentListProps) => {
    const cvEditContext = useContext(CVEditContext);

    return (
      <CVEditDocumentBaseList
        className={cvEditContext.layoutClassName}
        items={documentsSegmentStore.documentsField.store.value}
        OnRenderButtons={({ cveDocument }) => (
          <Buttons
            cveDocument={cveDocument}
            documentsSegmentStore={documentsSegmentStore}
          />
        )}
      />
    );
  }
);

interface ButtonsProps {
  cveDocument: CVEditDocument;
  documentsSegmentStore: CVEditDocumentsSegmentStore;
}

const Buttons = observer(
  ({ cveDocument, documentsSegmentStore }: ButtonsProps) => {
    const L = useLocStore(LocContext.CV);
    const cvEditContext = useContext(CVEditContext);
    const userInfo = useContext(UserInfoContext);
    const queryClient = useQueryClient();

    const documentsField = documentsSegmentStore.documentsField;

    const onDeleteDocumentClick = flow(function* (cveDocument: CVEditDocument) {
      const confirmed = yield wsConfirmDialog({
        userInfo: userInfo,
        header: L.id("d7aa402533db").loc("Bekreft sletting"),
        message: L.id("f5431ba15f48").loc("Dette kan ikke angres"),
        acceptLabel: L.id("e41945fe67ae").loc("Slett"),
        rejectLabel: L.id("da853c42f440").loc("Avbryt"),
        icon: "pi pi-trash",
      });

      if (!confirmed) return;

      const oldValue = documentsField.store.value;
      const newValue = oldValue.filter((doc) => doc.id !== cveDocument.id);

      const mutate = () =>
        userInfo.fullAPI.documentsAPI.deleteDocument({
          showSentryDialogOnError: true,
          userInfo: userInfo,
          documentId: cveDocument.id,
        });

      const invalidate = () =>
        invalidateQueries(queryClient, ["documentsAPI.getDocuments"]);

      return documentsField.valueVersioner.onChange({
        newValue,
        mutateBackend: mutate,
        invalidateQuery: invalidate,
        concurrentMutationGroup: "concurrentlyDeletingDocuments",
      });
    });

    return (
      <>
        <CVEditDownloadDocumentButton cveDocument={cveDocument} />
        <CVEditDeleteDocumentButton
          cveDocument={cveDocument}
          onDeleteDocumentClick={onDeleteDocumentClick}
        />
      </>
    );
  }
);
